import React from 'react';
import { Calendar, RotateCcw } from 'lucide-react';
import { Select, Autocomplete } from '@anyemp/ui-kit';
import DateRangePicker from '../UI/DateRangePicker';
import { Loader } from '../UI/LoadingSpinner';
import SavedFiltersDropdown from '../SavedFilters/SavedFiltersDropdown';

const PERIOD_OPTIONS = [
  { value: '', label: 'No grouping' },
  { value: 'day', label: 'By days' },
  { value: 'week', label: 'By weeks' },
  { value: 'month', label: 'By months' }
];

const DEFAULT_SAVED_FILTER_KEYS = ['startDate', 'endDate', 'manager_ids', 'period'];

/**
 * Shared filter panel for Manager* Stats components.
 *
 * Props:
 *   filters         — { startDate, endDate, manager_ids, period }
 *   managers        — [{ value, label }]
 *   managersLoading — bool
 *   viewMode        — 'list' | 'matrix' | 'charts'
 *   onFilterChange  — (updates: object) => void  — merge into parent filters state
 *   onApply         — () => void
 *   onReset         — () => void
 *   extraFilters    — optional React node (e.g. talent_types select for ManagerTalentsStats)
 *   pageKey         — optional saved-filters page key; when present, dropdown is shown
 *   savedFilterKeys — optional whitelist of keys to persist (defaults to base panel keys)
 */
const ManagerFilterPanel = ({
  filters,
  managers = [],
  managersLoading = false,
  viewMode = 'list',
  onFilterChange,
  onApply,
  onReset,
  extraFilters = null,
  pageKey,
  savedFilterKeys
}) => {
  const isMatrixOrCharts = viewMode === 'matrix' || viewMode === 'charts';

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Calendar className="inline h-4 w-4 mr-1" />
            Period
          </label>
          <DateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={(range) => onFilterChange({ startDate: range.startDate, endDate: range.endDate })}
          />
        </div>

        {/* Managers */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Managers
          </label>
          {managersLoading ? (
            <div className="flex items-center justify-center h-10">
              <Loader size="sm" />
            </div>
          ) : (
            <Autocomplete
              multiple
              clearable
              fullWidth
              options={managers}
              value={filters.manager_ids}
              onChange={(value) => onFilterChange({ manager_ids: value || [] })}
              placeholder="All managers"
            />
          )}
        </div>

        {/* Extra filters slot (e.g. talent_types) */}
        {extraFilters}

        {/* Period Grouping */}
        <Select
          label={
            <>
              Grouping
              {isMatrixOrCharts && <span className="text-xs text-gray-400 ml-1">(matrix columns)</span>}
            </>
          }
          fullWidth
          value={filters.period || ''}
          onChange={(v) => onFilterChange({ period: v || '' })}
          options={PERIOD_OPTIONS.filter(o => !isMatrixOrCharts || o.value !== '')}
        />
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2">
        {pageKey && (
          <SavedFiltersDropdown
            pageKey={pageKey}
            currentFilters={filters}
            applyKeys={savedFilterKeys || DEFAULT_SAVED_FILTER_KEYS}
            onApply={(saved) => onFilterChange(saved)}
            className="mr-auto"
          />
        )}
        <button
          onClick={onReset}
          className="flex min-h-10 items-center gap-1.5 rounded-lg bg-gray-100 px-4 py-2 text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </button>
        <button
          onClick={onApply}
          className="min-h-10 rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700"
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

export default ManagerFilterPanel;
